import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';

// Styles
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  TextField,
  Box,
  Badge,
  Autocomplete,
  CircularProgress,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Apis
import { drawUserInRaffle } from 'js/library/utils/API/Draws/drawUserInRaffle';
import { findStoreByProximity } from 'js/library/utils/API/CampaignLandinPage/apiFindStoreByProximity';
import { getRaffleParticipantsList } from 'js/library/utils/API/Draws/getRaffleParticipantsList';
import { getStoreItem } from 'js/library/utils/API/getStoreItem';
import { validateWinner } from 'js/library/utils/API/Draws/validateWinner';

export const DraggableAccordionDraw = ({
  drawSettings,
  form,
  setForm,
  expandedAccordion,
  setExpandedAccordion,
  handleDeleteDraw,
  handleChangeDraw,
  isNotRaffle,
}) => {
  const fixedDrawCount = drawSettings?.hasUsersList ? drawSettings?.raffleVouchers?.length || 0 : 0;

  const [actionLoading, setActionLoading] = useState({});
  const [loading, setLoading] = useState({});
  const [loadingRaffle, setLoadingRaffle] = useState({});
  const [inputSearch, setInputSearch] = useState({});
  const [optionsOpen, setOptionsOpen] = useState({});
  const [promotionsList, setPromotionsList] = useState({});
  const [raffleResults, setRaffleResults] = useState({});
  const [approvedDrawsCounterControl, setApprovedDrawsCounterControl] = useState(
    drawSettings?.approvedDrawsCounter ?? 0
  );

  const dataStore = async (input, index) => {
    setLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const result = await findStoreByProximity(input, true, 0, 10);
      const { response } = result;
      const filteredPromotions = response.filter((promotion) => promotion.id);

      setPromotionsList((prev) => ({
        ...prev,
        [index]: filteredPromotions,
      }));
    } catch (error) {
      console.error('Erro ao obter a lista de promoções:', error);
    } finally {
      setLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const timersRef = useRef({});

  const debounce = useCallback((func, delay) => {
    return (index, value) => {
      if (timersRef.current[index]) {
        clearTimeout(timersRef.current[index]);
      }
      timersRef.current[index] = setTimeout(() => {
        func(value, index); // Note a ordem
      }, delay);
    };
  }, []);

  const debouncedSearch = debounce(dataStore, 500);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = [...form.raffleVouchers];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({ ...item, priority: index }));

    setForm((prev) => ({
      ...prev,
      raffleVouchers: updatedItems,
    }));
  };

  const getSelectedPromotion = (draw, promotionOptions) => {
    return promotionOptions?.find((promotion) => promotion.id === draw.store) ?? null;
  };

  const handleRaffleClick = async (raffleIdentifier, index) => {
    setLoadingRaffle((prev) => ({ ...prev, [index]: true }));
    try {
      const result = await drawUserInRaffle(raffleIdentifier);
      const { winningUser } = result;

      const participantsResult = await getRaffleParticipantsList(raffleIdentifier, 0, 10000);
      const participantMatch = participantsResult.find(
        (participant) => participant.uid === winningUser.uid
      );

      if (participantMatch) {
        setRaffleResults((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            drawn: true,
            participant: participantMatch,
          },
        }));
      } else {
        toast.warn('Participante sorteado não encontrado na lista de participantes.');
      }
    } catch (error) {
      toast.error('Erro ao sortear participante');
      console.error(error);
    } finally {
      setLoadingRaffle((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleApproveWinner = async (index) => {
    setActionLoading((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || {}), approve: true, reject: false },
    }));
    try {
      await validateWinner(drawSettings.id, true);

      setRaffleResults((prev) => ({
        ...prev,
        [index]: { ...(prev[index] || {}), approve: true },
      }));

      setApprovedDrawsCounterControl((prev) => prev + 1);
      setExpandedAccordion((prev) => (prev !== null ? prev + 1 : 0));
      toast.success('Ganhador aprovado!');
    } catch (error) {
      toast.error(error?.message || 'Erro ao aprovar ganhador');
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [index]: { approve: false, reject: false },
      }));
    }
  };

  const handleRejectWinner = async (index) => {
    setActionLoading((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || {}), reject: true, approve: false },
    }));
    try {
      await validateWinner(drawSettings.id, false);

      setRaffleResults((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      toast.warning('Ganhador recusado!');
    } catch (error) {
      toast.error(error?.message || 'Erro ao recusar ganhador');
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [index]: { approve: false, reject: false },
      }));
    }
  };

  function formatPhoneNumber(phone) {
    const match = phone.match(/^\+(\d{2})(\d{2})(\d{5})(\d{4})$/);

    if (!match) return phone;

    const [, country, ddd, firstPart, secondPart] = match;

    return `+${country} (${ddd}) ${firstPart}-${secondPart}`;
  }

  useEffect(() => {
    if (drawSettings) {
      (async function loadInitialStoreItems() {
        const vouchers = form.raffleVouchers;

        const promises = vouchers.map(async (draw, index) => {
          if (draw.store) {
            try {
              setLoading((prev) => ({ ...prev, [index]: true }));
              const result = await getStoreItem(draw.store);

              if (result?.id) {
                setPromotionsList((prev) => ({
                  ...prev,
                  [index]: [result],
                }));
              }
            } catch (error) {
              console.error(`Erro ao carregar promoção para o store ${draw.store}:`, error);
            } finally {
              setLoading((prev) => ({ ...prev, [index]: false }));
            }
          }
        });

        await Promise.all(promises);
      })();
    }
  }, []);

  useEffect(() => {
    form.raffleVouchers.forEach((_, index) => {
      const value = inputSearch[index] || '';
      if (
        value.length > 2 &&
        !loading[index] &&
        !form.raffleVouchers[index].store &&
        !(promotionsList[index] && promotionsList[index].length)
      ) {
        debouncedSearch(index, value);
      }
    });
  }, [inputSearch, form.raffleVouchers]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    setExpandedAccordion(approvedDrawsCounterControl);
  }, []);

  useEffect(() => {
    if (approvedDrawsCounterControl && drawSettings.raffleVouchers?.length > 0) {
      const approvedCount = approvedDrawsCounterControl;
      const vouchers = drawSettings.raffleVouchers;

      const updatedRaffleResults = {};

      for (let i = 0; i < approvedCount; i++) {
        const voucher = vouchers[i];
        if (voucher?.winnerData) {
          updatedRaffleResults[i] = {
            drawn: true,
            winnerApproved: true,
            winnerData: voucher.winnerData,
          };
        }
      }

      setRaffleResults((prev) => ({
        ...prev,
        ...updatedRaffleResults,
      }));
    }
  }, [drawSettings]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="draw">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {form.raffleVouchers.map((draw, index) => (
              <Draggable key={index} draggableId={String(index)} index={index}>
                {(provided, snapshot) => {
                  const child = (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      width="100%"
                      mt="20px"
                      sx={{
                        background: snapshot.isDragging ? '#f0f0f0' : 'transparent',
                      }}
                    >
                      <Badge
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        badgeContent={index + 1}
                        color="secondary"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                        }}
                      >
                        <Accordion
                          expanded={expandedAccordion === index}
                          onChange={() =>
                            setExpandedAccordion(expandedAccordion === index ? -1 : index)
                          }
                        >
                          <AccordionSummary
                            sx={{
                              '& .MuiAccordionSummary-content': {
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                              },
                            }}
                            expandIcon={<ExpandMoreIcon />}
                          >
                            {!drawSettings?.hasUsersList ? (
                              <IconButton
                                {...provided.dragHandleProps}
                                aria-label="drag"
                                size="small"
                                color="primary"
                              >
                                <DragIndicatorIcon sx={{ fontSize: '35px', color: '#bbb' }} />
                              </IconButton>
                            ) : (
                              <span
                                {...provided.dragHandleProps}
                                style={{
                                  width: 0,
                                  height: 0,
                                  overflow: 'hidden',
                                  position: 'absolute',
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                width: 'max-content',
                                paddingLeft: '0px !important',
                                height: '100%',
                              }}
                            >
                              {draw.description || `Sorteio ${index + 1}`}
                            </Typography>

                            {form.raffleVouchers.length > 1 && index >= fixedDrawCount && (
                              <IconButton
                                aria-label="delete"
                                color="error"
                                onClick={() => handleDeleteDraw(index)}
                                sx={{ marginLeft: 'auto' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </AccordionSummary>
                          <AccordionDetails>
                            <Autocomplete
                              disabled={drawSettings?.hasUsersList && index < fixedDrawCount}
                              fullWidth
                              disablePortal
                              sx={{ mt: '15px', mb: '5px' }}
                              id="combo-box-demo"
                              options={promotionsList[index] || []}
                              value={getSelectedPromotion(draw, promotionsList[index])}
                              getOptionLabel={(option) =>
                                `Nome: ${option.title || ' Ainda não buscado'} | ID: ${
                                  option.id || ' Sem ID'
                                } | TemplateId: ${option.templateId || ' Sem template'}`
                              }
                              blurOnSelect
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={
                                    loading[index] ? (
                                      <>
                                        <CircularProgress size={20} color="inherit" />
                                        &nbsp;Pesquisando...
                                      </>
                                    ) : (
                                      'Promoção do prêmio'
                                    )
                                  }
                                  placeholder="Pesquise"
                                  disabled={
                                    loading[index] ||
                                    false ||
                                    (drawSettings?.hasUsersList && index < fixedDrawCount)
                                  }
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setInputSearch((prev) => ({ ...prev, [index]: value }));

                                    if (!value) {
                                      setPromotionsList((prev) => {
                                        const updatedPromotionsList = { ...prev };
                                        delete updatedPromotionsList[index];
                                        return updatedPromotionsList;
                                      });
                                    }
                                  }}
                                  onClick={() =>
                                    setOptionsOpen((prev) => ({ ...prev, [index]: true }))
                                  }
                                  onFocus={() =>
                                    setOptionsOpen((prev) => ({ ...prev, [index]: true }))
                                  }
                                  onBlur={() =>
                                    setOptionsOpen((prev) => ({ ...prev, [index]: false }))
                                  }
                                />
                              )}
                              ListboxProps={{ style: { maxHeight: '200px' } }}
                              open={optionsOpen[index] || false}
                              onClose={() =>
                                setOptionsOpen((prev) => ({
                                  ...prev,
                                  [index]: false,
                                }))
                              }
                              onChange={(_, newValue) => {
                                if (newValue) {
                                  handleChangeDraw(index, 'store', newValue.id);

                                  if (!draw.description) {
                                    handleChangeDraw(index, 'description', newValue.title);
                                  }

                                  setInputSearch((prev) => ({ ...prev, [index]: '' }));
                                } else {
                                  handleChangeDraw(index, 'store', '');
                                }
                              }}
                            />
                            <TextField
                              disabled={drawSettings?.hasUsersList && index < fixedDrawCount}
                              fullWidth
                              label="Descrição do prêmio*"
                              value={draw.description}
                              onChange={(e) =>
                                handleChangeDraw(index, 'description', e.target.value)
                              }
                              margin="normal"
                            />
                            {!isNotRaffle && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  mt: '0.8rem',
                                  flexDirection: 'column',
                                }}
                              >
                                {raffleResults[index]?.drawn ||
                                drawSettings.raffleVouchers[approvedDrawsCounterControl]
                                  ?.winnerData ? (
                                  <>
                                    <Typography
                                      variant="h5"
                                      color="primary"
                                      sx={{ fontWeight: 600, mb: '0.3rem' }}
                                    >
                                      Sorteado:{' '}
                                      <span style={{ color: '#000' }}>
                                        {raffleResults[index]?.winnerData?.aliasName ??
                                          raffleResults[index]?.participant?.aliasName ??
                                          drawSettings.raffleVouchers[approvedDrawsCounterControl]
                                            ?.winnerData?.aliasName ??
                                          'N/A'}
                                      </span>
                                    </Typography>
                                    <Typography
                                      variant="h5"
                                      color="primary"
                                      sx={{ fontWeight: 600, mb: '0.1rem' }}
                                    >
                                      Telefone:{' '}
                                      <span style={{ color: '#000' }}>
                                        {formatPhoneNumber(
                                          raffleResults[index]?.winnerData?.cellPhone ??
                                            raffleResults[index]?.participant?.cellPhone ??
                                            drawSettings.raffleVouchers[approvedDrawsCounterControl]
                                              ?.winnerData?.cellPhone
                                        ) || 'N/A'}
                                      </span>
                                    </Typography>
                                    <Typography
                                      variant="h5"
                                      color="primary"
                                      sx={{ fontWeight: 600, mb: '0.1rem' }}
                                    >
                                      Bilhete:{' '}
                                      <span style={{ color: '#000' }}>
                                        {raffleResults[index]?.winnerData?.userEntryId ??
                                          raffleResults[index]?.participant?.userEntryId ??
                                          drawSettings.raffleVouchers[approvedDrawsCounterControl]
                                            ?.winnerData?.userEntryId ??
                                          'N/A'}
                                      </span>
                                    </Typography>

                                    {index === (approvedDrawsCounterControl ?? 0) &&
                                      !raffleResults[index]?.winnerRejected &&
                                      !raffleResults[index]?.winnerApproved && (
                                        <Box sx={{ display: 'flex', gap: '1rem', mt: '1rem' }}>
                                          <Button
                                            onClick={() => handleApproveWinner(index)}
                                            disabled={
                                              actionLoading[index]?.approve ||
                                              actionLoading[index]?.reject
                                            }
                                            variant="contained"
                                            sx={{
                                              fontWeight: 600,
                                              minWidth: '10rem',
                                              letterSpacing: '1px',
                                            }}
                                          >
                                            {actionLoading[index]?.approve ? (
                                              <CircularProgress size={20} sx={{ color: '#fff' }} />
                                            ) : (
                                              'Aprovar Ganhador'
                                            )}
                                          </Button>
                                          <Button
                                            onClick={() => handleRejectWinner(index)}
                                            disabled={
                                              actionLoading[index]?.approve ||
                                              actionLoading[index]?.reject
                                            }
                                            variant="outlined"
                                            sx={{
                                              fontWeight: 600,
                                              minWidth: '10rem',
                                              letterSpacing: '1px',
                                            }}
                                          >
                                            {actionLoading[index]?.reject ? (
                                              <CircularProgress size={20} />
                                            ) : (
                                              'Recusar'
                                            )}
                                          </Button>
                                        </Box>
                                      )}
                                  </>
                                ) : (
                                  <Button
                                    disabled={index !== (approvedDrawsCounterControl ?? 0)}
                                    onClick={() => handleRaffleClick(drawSettings.id, index)}
                                    sx={{
                                      fontWeight: 600,
                                      minWidth: '10rem',
                                      letterSpacing: '1px',
                                    }}
                                    variant="contained"
                                  >
                                    {loadingRaffle[index] ? (
                                      <CircularProgress
                                        size={25}
                                        thickness={5}
                                        sx={{ color: '#fff' }}
                                      />
                                    ) : (
                                      'Sortear'
                                    )}
                                  </Button>
                                )}
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      </Badge>
                    </Box>
                  );
                  return snapshot.isDragging ? ReactDOM.createPortal(child, document.body) : child;
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
